import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { CommPreferenceQueries } from '@frontend/api-messaging';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { NakedButton, useTooltip } from '@frontend/design-system';

const iconButtonStyle = {
  height: 'var(--fabric-number-n-7-px)',
  width: 'var(--fabric-number-n-7-px)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--fabric-border-radius-small, 4px)',
};

const optOutIconButtonStyle = {
  background: 'var(--fabric-color-status-info-subtle)',
  ':hover': {
    background: 'var(--fabric-color-surface-selected-default)',
  },
};

type OptOutIndicatorInlineProps =
  | {
      /** Phone number to check opt-out status for */
      phoneNumber: string;
      /** Location ID for the preference check */
      locationId: string;
      /** Override tooltip label */
      tooltipLabel?: string;
      /** When true, skip the API check and always show the indicator */
      show?: never;
    }
  | {
      /** When true, skip the API check and always show the indicator */
      show: true;
      /** Override tooltip label */
      tooltipLabel?: string;
      phoneNumber?: never;
      locationId?: never;
    };

/**
 * Inline opt-out indicator icon with tooltip.
 *
 * Two usage modes:
 * 1. **Auto-detect** (default): Pass `phoneNumber` + `locationId` — component queries
 *    SMS preference and conditionally renders.
 * 2. **Explicit**: Pass `show={true}` — always renders without any API call.
 *    Useful when the parent already knows opt-out state (e.g. group thread header).
 */
export const OptOutIndicatorInline = (props: OptOutIndicatorInlineProps) => {
  const { t } = useTranslation('thread-header');
  const { tooltipLabel } = props;

  // Auto-detect mode: query opt-out status
  const shouldQuery = !props.show && !!props.phoneNumber && !!props.locationId;
  const normalizedPhone = shouldQuery ? formatPhoneNumberE164(props.phoneNumber!) : '';
  // When shouldQuery is false, empty strings disable the hook's built-in enabled check
  const { data: smsPreference } = CommPreferenceQueries.useCheckSMSPreference({
    userChannelAddress: normalizedPhone,
    channel: Channel.CHANNEL_SMS,
    locationId: shouldQuery ? props.locationId! : '',
    messageType: MessageType_Enum.MESSAGING_MANUAL,
  });

  const isOptedOut = props.show || smsPreference?.consented === false;

  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'top', hoverDelay: 300 });

  if (!isOptedOut) {
    return null;
  }

  return (
    <>
      <NakedButton css={[iconButtonStyle, optOutIconButtonStyle]} aria-label={t('Contact opted out')} {...triggerProps}>
        <Icon name='opt-out' size={16} color='primary' />
      </NakedButton>
      <Tooltip {...tooltipProps}>
        {tooltipLabel ?? t('This contact has opted out of receiving messages from you.')}
      </Tooltip>
    </>
  );
};
